import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components';
import { getUserEmail, extractApiErrorMessage } from '@/utils';
import { showSuccessToast, showErrorToast } from '@/components/toast/globalToast';
import {
  VehicleAlertComment,
  getVehicleAlertComments,
  createVehicleAlertComment,
  updateVehicleAlertComment,
} from '@/services/api/monitoring';

interface MonitoringCommentsProps {
  alertID: string;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const initialsOf = (email?: string): string => {
  if (!email) return '?';
  const name = email.split('@')[0] || email;
  const parts = name.split(/[._-]+/).filter(Boolean);
  const letters = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  return (letters || name.slice(0, 2)).toUpperCase();
};

const MonitoringComments: React.FC<MonitoringCommentsProps> = ({ alertID }) => {
  const [comments, setComments] = useState<VehicleAlertComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingID, setEditingID] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  const currentUser = getUserEmail();

  const loadComments = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getVehicleAlertComments(alertID);
      setComments(data);
    } catch (error) {
      console.error('Failed to load comments:', error);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  }, [alertID]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setIsSubmitting(true);
    try {
      await createVehicleAlertComment(alertID, {
        comment: newComment.trim(),
        createdBy: currentUser,
      });
      setNewComment('');
      showSuccessToast('Success', 'Comment added');
      await loadComments();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to add comment')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEdit = async (commentID: string) => {
    if (!editingText.trim()) return;
    setIsSubmitting(true);
    try {
      await updateVehicleAlertComment(alertID, commentID, {
        comment: editingText.trim(),
        updatedBy: currentUser,
      });
      setEditingID(null);
      setEditingText('');
      showSuccessToast('Success', 'Comment updated');
      await loadComments();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to update comment')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h2 className="text-[15px] font-semibold text-gray-900">Comments</h2>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[12px] font-medium text-gray-600">
          {comments.length}
        </span>
      </div>

      {/* Thread */}
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 min-h-[200px]">
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-[13px] text-gray-500">
            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <i className="ri-chat-3-line mb-2 text-[28px] text-gray-300"></i>
            <p className="text-[13px] text-gray-400">
              No comments yet. Start the conversation.
            </p>
          </div>
        ) : (
          comments.map((c) => (
            <div key={c.ID} className="flex gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-[11px] font-semibold text-primary">
                {initialsOf(c.createdBy)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[13px] font-medium text-gray-900">
                    {c.createdBy}
                  </span>
                  <span className="text-[11px] text-gray-400">
                    {formatDateTime(c.createdAt)}
                    {c.updatedAt ? ' · edited' : ''}
                  </span>
                </div>

                {editingID === c.ID ? (
                  <div className="mt-1">
                    <textarea
                      className="w-full rounded-md border border-gray-200 p-2 text-[14px] focus:outline-none focus:ring-1 focus:ring-primary"
                      rows={3}
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <Button
                        variant="white"
                        size="small"
                        onClick={() => {
                          setEditingID(null);
                          setEditingText('');
                        }}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => handleSaveEdit(c.ID)}
                        disabled={isSubmitting || !editingText.trim()}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-0.5 flex items-start justify-between gap-2">
                    <p className="whitespace-pre-wrap break-words text-[14px] text-gray-700">
                      {c.comment}
                    </p>
                    {c.createdBy === currentUser && (
                      <button
                        type="button"
                        className="flex-shrink-0 text-[12px] text-gray-400 hover:text-primary"
                        onClick={() => {
                          setEditingID(c.ID);
                          setEditingText(c.comment);
                        }}
                        aria-label="Edit comment"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-gray-100 px-5 py-4">
        <textarea
          className="w-full rounded-md border border-gray-200 p-2 text-[14px] focus:outline-none focus:ring-1 focus:ring-primary"
          rows={3}
          placeholder="Add a comment…"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          disabled={isSubmitting}
        />
        <div className="mt-2 flex justify-end">
          <Button
            variant="primary"
            size="small"
            onClick={handleAddComment}
            disabled={isSubmitting || !newComment.trim()}
          >
            <i className="ri-send-plane-line mr-1 text-[12px]"></i>
            Comment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MonitoringComments;
